import { Modal } from '@amzn/awsui-components-react';
import React from 'react';
import { FeedbackForm, SubmitHandler } from './FeedbackForm';
import { FORM_HEADER } from './constants';
import { Rating } from './types';

/**
 * The chat modal host.
 *
 * Every way of leaving this modal other than Submit is a **pure close**: no record, no counter, and nothing
 * surfaced. There is no `sendBeacon`, no `visibilitychange`/`pagehide` listener and no `history.listen` — an
 * exit path that recorded something is exactly what this design does not have.
 *
 * That includes Skip, which emits nothing — a deferral rather than an omission. `MerlinFeedbackTelemetry`
 * carries no skip emitter, and the two routes its existing emitters post to are not served yet, so a skip
 * counter belongs with that server work: this feature's client telemetry should arrive together rather than
 * a third emitter posting into the same gap.
 */

export interface FeedbackModalProps {
  visible: boolean;
  contextSubtitle?: React.ReactNode;
  initialRating?: Rating | null;
  formOpenId: string;
  onSubmit: SubmitHandler;
  /** Invoked by Skip, by the close control, by Escape, and by an overlay click alike. */
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  visible,
  contextSubtitle,
  initialRating = null,
  formOpenId,
  onSubmit,
  onClose,
}) => {
  /**
   * Tracked here by wrapping `onSubmit`, rather than by having the form report its state upwards.
   *
   * The form already freezes its own inputs while a submission is in flight, but the modal's close control,
   * Escape and overlay click bypassed that entirely and unmounted the form mid-write. The submission itself
   * still completed — nothing aborts the request — but the outcome was never shown, and on the timeout path
   * that outcome is the only place the user is told that retrying is safe. Losing it means someone who
   * dismissed by reflex has no way to learn whether their feedback landed.
   *
   * Wrapping keeps this inside the host: the form's contract is unchanged, and the one thing the host needs
   * to know is already passing through it.
   */
  const [submitting, setSubmitting] = React.useState(false);
  const handleSubmit = React.useCallback<SubmitHandler>(
    async (draft) => {
      setSubmitting(true);
      try {
        return await onSubmit(draft);
      } finally {
        // Cleared as soon as the promise settles, which is when the form has an outcome to render — so the
        // modal becomes dismissible exactly when there is something worth having seen.
        setSubmitting(false);
      }
    },
    [onSubmit]
  );
  return (
    <Modal
      visible={visible}
      header={FORM_HEADER}
      // Every dismissal route lands here, so they are indistinguishable by construction rather than by
      // each being handled the same way in three places — including in being refused mid-submission.
      onDismiss={() => {
        if (!submitting) onClose();
      }}
      data-testid="merlin-feedback-modal"
    >
      {visible ? (
        <FeedbackForm
          contextSubtitle={contextSubtitle}
          initialRating={initialRating}
          formOpenId={formOpenId}
          onSubmit={handleSubmit}
          /*
           * Skip is not wrapped. The form disables it while in flight, and a skip is a pure close with
           * nothing to await — so there is no outcome it could discard.
           */
          onSkip={() => onClose()}
        />
      ) : null}
    </Modal>
  );
};
